import { EUserType, IPage, ISort } from 'services/BackendApi';
import { IBootstrapCountry } from 'services/BackendApi/types/country';
import { IPagination } from 'store/common/types';
import { ETypeValueType, ICodeValue, ICrmLeadStage } from './types';
import { IBootstrapCurrency } from 'services/BackendApi/types/currency';
import { ICrmSearchTag } from './CrmTagTypes';

export enum ECrmDirectoryEntryType {
  COMPANY = 'COMPANY',
  PERSON = 'PERSON',
  BREAKDOWN_COMPANIES = 'BREAKDOWN_COMPANIES',
}

export enum ECrmDirectoryListSortableKey {
  NAME = 'name',
  PROFILE_TYPE = 'profileType',
  MANAGER = 'manager',
  COUNTRY = 'country',
  STAGE = 'leadStage',
  SCORE = 'leadScore',
  CREATED_AT = 'createdAt',
}

export interface ILeadCountryScoreItem {
  country: IBootstrapCountry | null;
  annualValueBookings: number | null;
  annualNumberBookings: number | null;
}

export enum ECrmTravelCompanyType {
  TOUR_OPERATOR = 'Tour Operator',
  TRAVEL_AGENT = 'Travel Agent',
  CONCIERGE = 'Concierge',
  PERSONAL_ASSISTANT = 'Personal Assistant',
  CONSORTIA = 'Consortia',
  OTHER = 'Other',
}

export enum ECrmCompanyType {
  TRAVEL_COMPANY = 'TC',
  ACTIVITIES = 'ACT',
  GROUND_SERVICES = 'GS',
  TRANSFERS = 'TRANSFER', // that is how this type is written in database migrations in crm-service
  HOTEL = 'HOTEL',
}

export enum EBookingActivityStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  NOT_CONFIRMED = 'NOT_CONFIRMED',
  NO_BOOKINGS = 'NO_BOOKINGS',
}

export interface ICrmPerson {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  type: EUserType;
}

export interface ICrmLead {
  /** Format: uuid */
  id: string;
  leadStageType: ICrmLeadStage;
  channelType: ICodeValue | null;
  sourceType: ICodeValue | null;
  scoring: number | null;
  countryScore: ILeadCountryScoreItem[];
}

export interface ILinkedDirectoryEntryArrayItem {
  /** Format: uuid */
  id: string;
  linkId: string;
  type: ECrmDirectoryEntryType;
  fullName: string;
  country: IBootstrapCountry | null;
  /** Format: date-time */
  createdDate: string;
  /** Format: date-time */
  publishedDate: string | null;
  email: string | null;
  role: ICodeValue | null;
  contactType: ICodeValue | null;
  companyType: ICodeValue | null;
}

export interface ITypeCode {
  value: string;
  profileType: ETypeValueType;
}

export interface IFilter {
  term: string;
  typeCodes: ITypeCode[];
  managerId: string | null;
  countryCodes: string[];
  leadStageCode: string | null;
  leadScoreGte: number | null;
  bookingActivityStatus: EBookingActivityStatus | null;
}

export interface IFilterSearch {
  term?: string;
  companyTypeCodes?: string[];
  contactTypeCodes?: string[];
  managerId?: string;
  countryCodes?: string[];
  leadStageCode?: string;
  leadScoreGte?: number | null;
  bookingActivityStatus?: EBookingActivityStatus;
}

export interface IDirectorySearchParams {
  filter: IFilterSearch;
  pagination: IPage;
  sort: ISort<ECrmDirectoryListSortableKey>;
}

export interface IFilterSimpleSearch {
  directoryEntryType: ECrmDirectoryEntryType;
  term: string;
  linkedDirectoryEntryId?: string;
}

export interface IFilterFullSearch {
  term: string;
  companyTypeCodes: string[];
}

export interface IDirectorySimpleSearchParams {
  filter: IFilterSimpleSearch;
  pagination: IPage;
}

export interface ICrmCompanyProfile {
  /** Format: uuid */
  id: string;
  type: string;
  companyType: ICodeValue;
  tcType: ECrmTravelCompanyType;
  name: string;
  industry: string | null;
  description: string | null;
  employeeCount: number | null;
  website: string | null;
}

export interface ICrmContactProfile {
  id: string;
  type: string;
  contactType: ICodeValue;
  firstName: string;
  lastName: string | null;
  titleType: ICodeValue | null;
  landline: string | null;
  region: string | null;
  /** Format: date */
  birthday: string | null;
  personalDocumentType: ICodeValue | null;
  personalDocumentNumber: string | null;
  personalDocumentCountry: ICodeValue | null;
  role: ICodeValue | null;
  /** Format: date */
  roleStart: string | null;
  /** Format: date */
  roleEnd: string | null;
}

export interface IDirectoryEntrySearchResult {
  id: string;
  directoryEntryType: ECrmDirectoryEntryType;
  profileId: string;
  profileType: ICodeValue;
  name: string;
  email: string | null;
  beHumanReadableId: string | null;
  country: IBootstrapCountry | null;
  leadStage: ICrmLeadStage | null;
  leadScore: number | null;
  manager: ICrmPerson;
  createdAt: string;
  linkedDirectoryEntries: ILinkedDirectoryEntry[];
  bookingActivityStatus: EBookingActivityStatus | null;
  lastBookingConfirmedDate: string | null;
  lastBookingRequestedDate: string | null;
}

export interface ILinkedDirectoryEntry {
  id: string;
  type: ECrmDirectoryEntryType;
  fullName: string;
  email: string | null;
}

export interface IDirectoryEntry {
  /** Format: uuid */
  id: string;
  manager: ICrmPerson;
  type: ECrmDirectoryEntryType;
  fullName: string;
  /** Format: email */
  email: string | null;
  acceptsEmail: boolean;
  country: IBootstrapCountry;
  mobile: string | null;
  address: string | null;
  linkedin: string | null;
  twitter: string | null;
  instagram: string | null;
  /** Format: date-time */
  createdDate: string;
  createdBy: ICrmPerson;
  /** Format: date-time */
  updatedDate: string | null;
  updatedBy: ICrmPerson;
  publishedDate: string | null;
  publishedBy: ICrmPerson;
  beHumanReadableId: string | null;
  beId: string | null;
  keyNotes: string;
  profile: ICrmCompanyProfile | ICrmContactProfile;
  lead: ICrmLead;
  linkedDirectoryEntries: ILinkedDirectoryEntryArrayItem[];
  currency: IBootstrapCurrency;
  subscribers: ICrmPerson[];
  tags: ICrmSearchTag[];
}

export interface IDirectoryEntryLinked {
  /** Format: uuid */
  id: string;
  type: ECrmDirectoryEntryType;
  fullName: string;
  /** Format: email */
  email: string | null;
  companyType: string | null;
  contactType: string | null;
  roleType: string | null;
}

export interface IDirectorySearchResponse {
  list: IDirectoryEntrySearchResult[];
  pagination: IPagination;
  count: number;
}

export interface IDirectoryEntrySimpleSearchResult {
  /** Format: uuid */
  id: string;
  directoryEntryType: ECrmDirectoryEntryType;
  name: string;
  email: string | null;
  beHumanReadableId: string | null;
}

export interface IDirectorySimpleSearchResponse {
  list: IDirectoryEntrySimpleSearchResult[];
  pagination: IPagination;
}

export interface IUpsertLink {
  sourceId: string;
  destinationId: string;
  linkTypeCode: string;
  reference: string | null;
}

export interface IUpsertLinkResponse {
  id: string;
  source: IDirectoryEntryLinked;
  destination: IDirectoryEntryLinked;
  linkType: ICodeValue;
  user: ICrmPerson;
  /** Format: date-time */
  date: string | null;
  reference: string | null;
}
